import { createAsyncThunk, isAllOf, isPending, isFulfilled, isRejected } from '@reduxjs/toolkit';
import { QueryStatus } from './apiState.js';
import { HandledError } from '../HandledError.js';
import { isDraftable, enablePatches, produceWithPatches } from 'immer';

function defaultTransformResponse(baseQueryReturnValue) {
  return baseQueryReturnValue;
}

function buildThunks({
  reducerPath,
  baseQuery,
  context: {
    endpointDefinitions
  },
  serializeQueryArgs,
  api
}) {
  const patchQueryResult = (endpointName, args, patches) => dispatch => {
    const endpoint = endpointDefinitions[endpointName];
    dispatch(api.internalActions.queryResultPatched({
      queryCacheKey: serializeQueryArgs({
        queryArgs: args,
        internalQueryArgs: endpoint.query(args),
        endpoint: endpointName
      }),
      patches
    }));
  };

  const updateQueryResult = (endpointName, args, updateRecipe) => (dispatch, getState) => {
    const currentState = api.endpoints[endpointName].select(args)(getState());
    let ret = {
      patches: [],
      inversePatches: []
    };

    if (currentState.status === QueryStatus.uninitialized) {
      return ret;
    }

    if ('data' in currentState) {
      if (isDraftable(currentState.data)) {
        // call "enablePatches" as late as possible
        enablePatches();
        const [, patches, inversePatches] = produceWithPatches(currentState.data, updateRecipe);
        ret.patches.push(...patches);
        ret.inversePatches.push(...inversePatches);
      } else {
        const value = updateRecipe(currentState.data);
        ret.patches.push({
          op: 'replace',
          path: [],
          value
        });
        ret.inversePatches.push({
          op: 'replace',
          path: [],
          value: currentState.data
        });
      }
    }

    dispatch(patchQueryResult(endpointName, args, ret.patches));
    return ret;
  };

  const queryThunk = createAsyncThunk(`${reducerPath}/executeQuery`, async (arg, {
    signal,
    rejectWithValue,
    ...api
  }) => {
    const endpoint = endpointDefinitions[arg.endpoint];
    const context = {};
    const queryApi = { ...api,
      context
    };
    if (endpoint.onStart) endpoint.onStart(arg.originalArgs, queryApi);

    try {
      var _endpoint$transformRe;

      const result = await baseQuery(arg.internalQueryArgs, {
        signal,
        dispatch: api.dispatch,
        getState: api.getState
      }, endpoint.extraOptions);
      if (result.error) throw new HandledError(result.error);
      if (endpoint.onSuccess) endpoint.onSuccess(arg.originalArgs, queryApi, result.data);
      return {
        fulfilledTimeStamp: Date.now(),
        result: await ((_endpoint$transformRe = endpoint.transformResponse) != null ? _endpoint$transformRe : defaultTransformResponse)(result.data)
      };
    } catch (error) {
      if (endpoint.onError) endpoint.onError(arg.originalArgs, queryApi, error instanceof HandledError ? error.value : error);

      if (error instanceof HandledError) {
        return rejectWithValue(error.value);
      }

      throw error;
    }
  }, {
    condition(arg, {
      getState
    }) {
      var _state$queries, _arg$forceRefetch;

      const state = getState()[reducerPath];
      const requestState = state == null ? void 0 : (_state$queries = state.queries) == null ? void 0 : _state$queries[arg.queryCacheKey];
      const baseFetchOnMountOrArgChange = state.config.refetchOnMountOrArgChange;
      const fulfilledVal = requestState == null ? void 0 : requestState.fulfilledTimeStamp;
      const refetchVal = (_arg$forceRefetch = arg.forceRefetch) != null ? _arg$forceRefetch : arg.subscribe && baseFetchOnMountOrArgChange; // Don't retry a request that's currently in-flight

      if ((requestState == null ? void 0 : requestState.status) === 'pending') return false; // Pull from the cache unless we explicitly force refetch or qualify based on time

      if (fulfilledVal) {
        if (refetchVal) {
          // Return if its true or compare the dates because it must be a number
          return refetchVal === true || (Number(new Date()) - Number(fulfilledVal)) / 1000 >= refetchVal;
        } // Value is cached and we didn't specify to refresh, skip it.


        return false;
      }

      return true;
    },

    dispatchConditionRejection: true
  });
  const mutationThunk = createAsyncThunk(`${reducerPath}/executeMutation`, async (arg, {
    signal,
    rejectWithValue,
    ...api
  }) => {
    const endpoint = endpointDefinitions[arg.endpoint];
    const context = {};
    const mutationApi = { ...api,
      context
    };
    if (endpoint.onStart) endpoint.onStart(arg.originalArgs, mutationApi);

    try {
      var _endpoint$transformRe2;

      const result = await baseQuery(arg.internalQueryArgs, {
        signal,
        dispatch: api.dispatch,
        getState: api.getState
      }, endpoint.extraOptions);
      if (result.error) throw new HandledError(result.error);
      if (endpoint.onSuccess) endpoint.onSuccess(arg.originalArgs, mutationApi, result.data);
      return {
        fulfilledTimeStamp: Date.now(),
        result: await ((_endpoint$transformRe2 = endpoint.transformResponse) != null ? _endpoint$transformRe2 : defaultTransformResponse)(result.data)
      };
    } catch (error) {
      if (endpoint.onError) endpoint.onError(arg.originalArgs, mutationApi, error instanceof HandledError ? error.value : error);

      if (error instanceof HandledError) {
        return rejectWithValue(error.value);
      }

      throw error;
    }
  });

  const hasTheForce = options => 'force' in options;

  const hasMaxAge = options => 'ifOlderThan' in options;

  const prefetchThunk = (endpointName, arg, options) => (dispatch, getState) => {
    const force = hasTheForce(options) && options.force;
    const maxAge = hasMaxAge(options) && options.ifOlderThan;

    const queryAction = (force = true) => api.endpoints[endpointName].initiate(arg, {
      forceRefetch: force
    });

    const latestStateValue = api.endpoints[endpointName].select(arg)(getState());

    if (force) {
      dispatch(queryAction());
    } else if (maxAge) {
      const lastFulfilledTs = latestStateValue == null ? void 0 : latestStateValue.fulfilledTimeStamp;

      if (!lastFulfilledTs) {
        dispatch(queryAction());
        return;
      }

      const shouldRetrigger = (Number(new Date()) - Number(new Date(lastFulfilledTs))) / 1000 >= maxAge;

      if (shouldRetrigger) {
        dispatch(queryAction());
      }
    } else {
      // If prefetching with no options, just let it try
      dispatch(queryAction(false));
    }
  };

  function matchesEndpoint(endpoint) {
    return action => {
      var _action$meta, _action$meta$arg;

      return (action == null ? void 0 : (_action$meta = action.meta) == null ? void 0 : (_action$meta$arg = _action$meta.arg) == null ? void 0 : _action$meta$arg.endpoint) === endpoint;
    };
  }

  function buildMatchThunkActions(thunk, endpoint) {
    return {
      matchPending: isAllOf(isPending(thunk), matchesEndpoint(endpoint)),
      matchFulfilled: isAllOf(isFulfilled(thunk), matchesEndpoint(endpoint)),
      matchRejected: isAllOf(isRejected(thunk), matchesEndpoint(endpoint))
    };
  }

  return {
    queryThunk,
    mutationThunk,
    prefetchThunk,
    updateQueryResult,
    patchQueryResult,
    buildMatchThunkActions
  };
}

export { buildThunks };
//# sourceMappingURL=buildThunks.js.map
